import { Component, OnDestroy, OnInit, Renderer2, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Location } from '@angular/common';

import { combineLatest, iif, Observable, of, Subscription } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

import { LanguageService } from '@fcom/ui-translate';
import { ConfigService } from '@fcom/core/services';
import { CmsContent, CmsImageData, CmsTemplate, CmsViewType } from '@fcom/core-api';
import {
  BreadcrumbService,
  CmsPageComponentMetaData,
  NavigationMenuService,
  PageMetaService,
  SchemaService,
} from '@fcom/common/services';
import { BreadcrumbItem } from '@fcom/common/interfaces';
import { isPresent, ReplaceParams, safeMap, unique, unsubscribe } from '@fcom/core/utils';
import {
  constructOndHeadingPath,
  encodeIgnoredSEOTitle,
  getFlightType,
  getLocationName,
  isCountryCode,
  isDestinationCode,
  transformLocationCode,
} from '@fcom/ond-marketing-landing-core/services/utils';
import {
  FlightPair,
  OndLocationData,
  OndLocationsResponse,
  OndMarketingRoutesPath,
  OndPageType,
  OndPairEnhancedResponse,
} from '@fcom/ond-marketing-landing-core/interfaces';
import { OndMarketingOffersService } from '@fcom/ond-marketing-landing-core/services';
import { finShare } from '@fcom/rx';
import { BookingLocationCodes } from '@fcom/common/interfaces/booking';
import { CmsTemplateService, findByType } from '@fcom/cms';

@Component({
  templateUrl: 'ond-marketing-landing.component.html',
  styleUrls: ['ond-marketing-landing.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class OndMarketingLandingComponent implements OnInit, OnDestroy {
  picture$: Observable<CmsImageData | null>;
  heading$: Observable<string>;
  flightParams$: Observable<FlightPair>;
  locations$: Observable<OndLocationsResponse>;
  bookingWidgetLocations$: Observable<BookingLocationCodes[]>;
  links$: Observable<OndPairEnhancedResponse>;
  ondPageType: OndPageType = OndPageType.LANDING;
  description: string;
  url$ = this.route.url;
  urlMetaData: Array<CmsPageComponentMetaData> = [];
  campaignImage$: Observable<CmsImageData>;
  defaultCountryImage$: Observable<CmsImageData>;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private ondMarketingOffersService: OndMarketingOffersService,
    private languageService: LanguageService,
    private pageMetaService: PageMetaService,
    private configService: ConfigService,
    private navigationMenuService: NavigationMenuService,
    private breadcrumbService: BreadcrumbService,
    private locationService: Location,
    private renderer: Renderer2,
    private router: Router,
    private cmsTemplateService: CmsTemplateService,
    private schemaService: SchemaService
  ) {}

  ngOnInit(): void {
    this.flightParams$ = this.route.params.pipe(
      switchMap((params) => this.paramsToFlightPair(params)),
      finShare()
    );

    this.links$ = this.flightParams$.pipe(
      switchMap((params) => {
        return this.createPairStream(params);
      }),
      finShare()
    );

    this.campaignImage$ = this.cmsTemplateService.load(`/${this.languageService.langValue}`).pipe(
      map((template: CmsTemplate) => safeMap(template, (t: CmsTemplate) => t.main)),
      filter(isPresent),
      finShare(),
      findByType(CmsViewType.FRONT_PAGE_OFFER, false),
      map((campaign: CmsContent) => campaign?.picture || null)
    );

    this.defaultCountryImage$ = this.flightParams$.pipe(
      switchMap((params) => iif(() => params.origin.length > 2, of(null), this.campaignImage$))
    );

    this.locations$ = this.flightParams$.pipe(
      switchMap((pair) => {
        const vitalLocations: string[] = pair.destination ? [pair.origin, pair.destination] : [pair.origin];
        return this.ondMarketingOffersService.getLocationData(vitalLocations);
      }),
      finShare()
    );

    const ondStream$ = combineLatest([this.flightParams$, this.locations$]).pipe(finShare());

    this.overrideBreadcrumb(ondStream$);

    this.bookingWidgetLocations$ = this.flightParams$.pipe(
      switchMap((flightParams) => {
        return of([
          {
            originLocationCode: this.prefillBookingWidgetField(flightParams.origin),
            destinationLocationCode: this.prefillBookingWidgetField(flightParams.destination),
          },
        ]);
      })
    );

    this.heading$ = ondStream$.pipe(
      switchMap(([flightParams, locationData]) => {
        return this.languageService.translate(constructOndHeadingPath('bookFlights', flightParams), {
          origin: getLocationName(flightParams.origin, locationData),
          destination: getLocationName(flightParams.destination, locationData),
          originLocationCode: flightParams.origin,
          destinationLocationCode: flightParams.destination,
        });
      }),
      finShare()
    );

    this.picture$ = combineLatest([ondStream$, this.defaultCountryImage$]).pipe(
      map(([[flightParams, locationData], defaultCountryImage]) => {
        const mainLocationData = locationData[flightParams.destination] || locationData[flightParams.origin];
        return (mainLocationData as OndLocationData)?.picture
          ? (mainLocationData as OndLocationData).picture
          : defaultCountryImage;
      })
    );
    const metaData$ = ondStream$.pipe(
      switchMap(([flightParams, locationData]): Observable<string[]> => this.getPageMeta(flightParams, locationData))
    );

    this.subscriptions.add(
      combineLatest([this.flightParams$, metaData$, this.route.queryParams, this.route.url, this.links$]).subscribe(
        ([activeParams, [title, description, url], queryParams, urlSegments, pairsResponse]) => {
          if (activeParams.destinationAirport && activeParams.originAirport) {
            this.schemaService.addFlightSchema({
              departureAirportName: activeParams.originAirport,
              departureAirportIataCode: activeParams.origin,
              arrivalAirportName: activeParams.destinationAirport,
              arrivalAirportIataCode: activeParams.destination,
            });
          }

          const pairs = Object.values(pairsResponse);
          if (OndMarketingRoutesPath.CITY_TO_CITY === urlSegments[0].path) {
            const lang: string = this.languageService.langValue;
            pairs
              .filter((pair) => lang === pair.lang)
              .forEach((pair) => {
                if (pair.title) {
                  title = pair.title;
                }

                if (pair.htmlDescription) {
                  description = pair.htmlDescription;
                }

                if (pair.detailText) {
                  this.description = pair.detailText;
                }
              });
          }

          if (!activeParams.ignoredSEOTitle) {
            const encodedUrl = encodeIgnoredSEOTitle(url);
            const orgPath = this.router.url.split('?')[0] ? this.router.url.split('?')[0] : this.router.url;
            const newUrl = `${orgPath}/${encodedUrl}`;
            this.router.navigate([newUrl], { queryParams: queryParams });
          } else {
            this.pageMetaService.moveMetaAndLinksToHead(
              [
                {
                  attributes: {
                    content: this.router.url,
                    property: 'og:url',
                  },
                  tagName: 'meta',
                },
                {
                  attributes: {
                    rel: 'canonical',
                    href: this.router.url,
                  },
                  tagName: 'link',
                },
                ...pairs
                  .map((pair) => pair.lang)
                  .filter(unique)
                  .map((language) => {
                    const formattedLang = this.configService.cfg.allowedLocales[language]?.replace('_', '-');
                    const pathArray = this.locationService.path().split('/');
                    const newPath = pathArray.splice(2);

                    return {
                      attributes: {
                        rel: 'alternate',
                        hreflang: formattedLang ?? language,
                        href: `${this.configService.cfg.baseUrl}/${language}/${newPath.join('/')}`,
                      },
                      tagName: 'link',
                    };
                  }),
              ],
              this.renderer
            );
          }
          this.pageMetaService.setTitle(title);
          this.pageMetaService.setDescription(description);
        }
      )
    );
  }

  prefillBookingWidgetField(locationCode: string): string | undefined {
    return isDestinationCode(locationCode) ? locationCode : undefined;
  }

  paramsToFlightPair(params: Params): Observable<FlightPair> {
    return combineLatest([
      this.ondMarketingOffersService.getAirportName(
        transformLocationCode(params.originLocationCode),
        this.languageService.langKeyValue
      ),
      this.ondMarketingOffersService.getAirportName(
        transformLocationCode(params.destinationLocationCode),
        this.languageService.langKeyValue
      ),
    ]).pipe(
      map(([originAirport, destinationAirport]) => {
        return {
          origin: transformLocationCode(params.originLocationCode) || '',
          originAirport: originAirport,
          destinationAirport: destinationAirport,
          destination: transformLocationCode(params.destinationLocationCode) || '',
          ignoredSEOTitle: params.ignoredSEOTitle || '',
        };
      }),
      finShare()
    );
  }

  getPageMeta(flightParams: FlightPair, locationData: OndLocationsResponse): Observable<string[]> {
    const translationData: ReplaceParams = {
      origin: getLocationName(flightParams.origin, locationData),
      originLocationCode: flightParams.origin,
      destination: getLocationName(flightParams.destination, locationData),
      destinationLocationCode: flightParams.destination,
    };

    const flightType = getFlightType(flightParams);
    return combineLatest([
      this.languageService.translate(`ondMarketing.metaData.${flightType}.title`, translationData),
      this.languageService.translate(`ondMarketing.metaData.${flightType}.description`, translationData),
      this.languageService.translate(`ondMarketing.seoUrl.${getFlightType(flightParams, false)}`, translationData),
    ]);
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  private createPairStream(flightParams: FlightPair): Observable<OndPairEnhancedResponse> {
    let originLocationCode: string | undefined;
    let destinationLocationCode: string | undefined;
    let originCountry: string | undefined;
    let destinationCountry: string | undefined;

    if (!isCountryCode(flightParams.origin)) {
      originLocationCode = flightParams.origin;
      destinationLocationCode = flightParams.destination ? flightParams.destination : undefined;
    } else {
      originCountry = flightParams.origin;
      destinationCountry = flightParams.destination ? flightParams.destination : undefined;
    }

    return this.ondMarketingOffersService.fetchLocationPairs({
      origin: originLocationCode,
      destination: destinationLocationCode,
      originCountry,
      destinationCountry,
    });
  }

  private overrideBreadcrumb(ondStream$: Observable<[FlightPair, OndLocationsResponse]>) {
    this.subscriptions.add(
      combineLatest([
        this.navigationMenuService.deprecatedDestinationsBreadcrumbItem$,
        this.navigationMenuService.currentPath$,
        this.languageService.translate('flightsTo'),
        ondStream$,
      ]).subscribe(([dst, cp, totr, [flightParams, locationData]]) => {
        if (isDestinationCode(flightParams.origin) && isDestinationCode(flightParams.destination)) {
          const destinationLocationData = locationData[flightParams.destination] as OndLocationData;
          const originLocationData = locationData[flightParams.origin] as OndLocationData;
          const countryCrumb: BreadcrumbItem = {
            title: destinationLocationData.country,
            url: destinationLocationData.countryUrl,
          };
          const destinationCrumb: BreadcrumbItem = {
            title: `${totr} ${destinationLocationData.cityName}`,
            url: destinationLocationData.destinationUrl,
          };
          const currentCrumb: BreadcrumbItem = {
            title: `${originLocationData.cityName} ${destinationLocationData.cityName}`,
            url: cp,
          };

          this.breadcrumbService.setBreadcrumb([dst, countryCrumb, destinationCrumb, currentCrumb]);
        } else {
          this.breadcrumbService.setBreadcrumb([dst]);
        }
      })
    );
  }
}
